// Time › Approvals — the two header verbs, "Approve submitted" and "Remind"
// (Karan 25/10/2026; spec §7.5). Pure, so the counts and the gates are tested
// without a page (test/time-approvals.test.mjs). The screen is
// views/timesheet-review.js; the writes are the per-row Approve's own
// (db.updateTimesheet, rules-guarded) and the remindTimesheets callable.

// The roles firestore.rules reviewer() lets approve a timesheet (= nav MONEY).
export const APPROVER_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];
export const canApprove = (roles) => Array.isArray(roles) && roles.some((r) => APPROVER_ROLES.includes(r));

const people = (n) => (n === 1 ? '1 person' : `${n} people`);
const sheetsWord = (n) => (n === 1 ? '1 timesheet' : `${n} timesheets`);

/**
 * What the header buttons say and whether they work.
 *   active   — the team (room screens already left out)
 *   byEmail  — the week's sheets by userEmail
 *   roles    — the signed-in person's roles
 *   mailbox  — the company has the automation mailbox (workspaceMailbox)
 *   started  — the week has begun; pastDeadline — the Tuesday lock has passed
 */
export function bulkState({ active, byEmail, roles, mailbox, started, pastDeadline }) {
  const submitted = active.filter((u) => byEmail[u.email] && byEmail[u.email].status === 'submitted');
  const noSheet = active.filter((u) => !byEmail[u.email]);
  const approver = canApprove(roles);
  const approve = {
    label: submitted.length ? `Approve ${submitted.length} submitted` : 'Approve submitted',
    enabled: approver && submitted.length > 0,
    title: !approver ? 'Only the people who approve timesheets can do this'
      : submitted.length ? `Approve every submitted timesheet for this week (${sheetsWord(submitted.length)})` : 'Nothing submitted to approve',
  };
  const remindWhy = !approver ? 'Only the people who approve timesheets can send reminders'
    : !mailbox ? 'Reminders need the automation mailbox, which is off for your company'
      : !started ? 'This week hasn’t started yet'
        : pastDeadline ? 'This week has locked: unlock their week instead'
          : null;
  const remind = {
    label: noSheet.length ? `Remind ${people(noSheet.length)}` : 'Remind',
    enabled: !remindWhy && noSheet.length > 0,
    rowEnabled: !remindWhy,
    title: remindWhy || (noSheet.length ? `Email ${people(noSheet.length)} with no timesheet for this week` : 'Everyone has a timesheet in'),
  };
  return { submitted, noSheet, approve, remind };
}

export const approveButtonLabel = (n) => `Approve ${sheetsWord(n)}`;

/**
 * Approve one by one — never all-or-nothing. `approveOne(item)` is the
 * per-row write. A failure is recorded and the rest carry on.
 * Returns { done: [item], failed: [{ item, error }] }.
 */
export async function approveEach(items, approveOne, onProgress = () => {}) {
  const done = []; const failed = [];
  for (const [i, item] of items.entries()) {
    onProgress({ index: i, item, state: 'working' });
    try {
      await approveOne(item);
      done.push(item); onProgress({ index: i, item, state: 'done' });
    } catch (e) {
      const error = (e && e.message) || String(e);
      failed.push({ item, error }); onProgress({ index: i, item, state: 'failed', error });
    }
  }
  return { done, failed };
}
